import { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { listUseCases } from "../../use-cases/registry";
import { hrefForUseCase } from "../../use-cases/links";

const USE_CASES = listUseCases();

interface UseCasesMenuProps {
  /** Color classes for the trigger; the navs differ only in tone. */
  triggerClassName: string;
}

/**
 * "Use Cases" dropdown shared by LandingNav and CaseStudyNav. Below `sm` the
 * text trigger collapses to a menu icon so the logo and CTA fit at 375px.
 *
 * Uses the disclosure pattern (button + `aria-expanded`/`aria-controls`
 * over a plain list of links) rather than ARIA `menu` roles, which would
 * promise arrow-key navigation this doesn't implement.
 */
export const UseCasesMenu = ({ triggerClassName }: UseCasesMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const panelId = useId();

  // Close on outside click + Escape.
  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e: PointerEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      className="relative"
      // Hover-to-open is for mice only; a tap would otherwise open on the
      // emulated enter and immediately close again on the click toggle.
      onPointerEnter={(e) => e.pointerType === "mouse" && setIsOpen(true)}
      onPointerLeave={(e) => e.pointerType === "mouse" && setIsOpen(false)}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-label="Use Cases"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`${triggerClassName} text-xs font-semibold tracking-widest uppercase whitespace-nowrap transition-colors flex items-center gap-1.5 p-1 -m-1 sm:p-0 sm:m-0`}
      >
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          className="sm:hidden w-5 h-5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
        >
          <path d="M3 5.5h14M3 10h14M3 14.5h14" />
        </svg>
        <span className="hidden sm:inline">Use Cases</span>
        <span
          aria-hidden="true"
          className={`hidden sm:inline text-[0.55rem] transition-transform ${isOpen ? "rotate-180" : ""}`}
        >
          ▾
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          // Mobile: span the nav (the icon sits mid-row, so anchoring to it
          // would push the panel off-screen). `fixed` resolves against the
          // nav, which is itself fixed at the top edge. Desktop: the
          // `before:` strip bridges the mt-2 gap so hover survives the move
          // from trigger to panel.
          <motion.div
            id={panelId}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-x-4 top-16 sm:absolute sm:inset-x-auto sm:left-0 sm:top-full mt-2 sm:min-w-[14rem] sm:before:absolute sm:before:inset-x-0 sm:before:-top-3 sm:before:h-3 bg-fwd-white rounded-md shadow-[0_8px_30px_rgba(17,17,24,0.12)] border border-fwd-ink/10 py-2 z-50"
          >
            {USE_CASES.map((useCase) => (
              <a
                key={useCase.slug}
                href={hrefForUseCase(useCase.slug)}
                className="block px-4 py-2.5 text-fwd-navy text-sm font-medium hover:text-fwd-red hover:bg-fwd-soft transition-colors"
              >
                {useCase.menuLabel}
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
